import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  TablePagination,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import Navbar from '../../../globals/components/Navbar';
import {
  ENHANCEMENT_STATUS_COLORS,
  formatEnhancementDate,
  getEnhancementFileColor,
  getEnhancementFileIcon,
  downloadEnhancementAttachmentFromUrl,
} from '../../SHARED/enhancementRequestUtils';
import { apiClient } from '../../../api/httpClient';
import { toast } from 'react-toastify';
import TablePaginationActions from "../../../globals/components/TablePaginationActions";

const ViewEnhancementRequest = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [updatedRequest, setUpdatedRequest] = useState({});
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    fetchEnhancementRequests();
  }, []);

  useEffect(() => {
    let filtered = requests;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(request => 
        request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.requester_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.requester_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
    setPage(0); // Reset to first page when filters change
  }, [searchTerm, statusFilter, requests]);

  const fetchEnhancementRequests = async () => {
    try {
      setLoading(true);
      const response = await apiClient('/enhancement-requests/list/', 'GET');
      if (response) {
        setRequests(response);
        setFilteredRequests(response);
      }
    } catch (error) {
      toast.error('Failed to fetch enhancement requests');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setUpdatedRequest({ ...request });
    setOpenDialog(true);
    setEditMode(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setFiles([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedRequest({ ...updatedRequest, [name]: value });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (editMode) {
      // Reset to original values if cancelling edit
      setUpdatedRequest({ ...selectedRequest });
      setFiles([]);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      const formDataToSend = new FormData();
      
      // Append all updated fields except attachments array
      Object.entries(updatedRequest).forEach(([key, value]) => {
        if (key !== 'id' && key !== 'created_at' && key !== 'attachments' && value !== null) {
          formDataToSend.append(key, value);
        }
      });
      
      // Append multiple files with correct field name
      files.forEach(file => {
        formDataToSend.append('attachments', file);
      });

      if (!selectedRequest?.request_id) return;

      const response = await apiClient(
        `/enhancement-requests/${selectedRequest.request_id}/`,
        'PATCH',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response) {
        toast.success('Enhancement request updated successfully');
        fetchEnhancementRequests();
        setSelectedRequest(response);
        setEditMode(false);
        setFiles([]);
      }
    } catch (error) {
      toast.error('Failed to update enhancement request');
      console.error('Update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = downloadEnhancementAttachmentFromUrl;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statusColors = ENHANCEMENT_STATUS_COLORS;

  return (
    <>
      <Navbar />
      <Box sx={{ padding: '2rem' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Enhancement Requests
          </Typography>
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status Filter"
                sx={{
                  borderRadius: '10px',
                }}
              >
                <MenuItem value="All">All Statuses</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              variant="outlined"
              placeholder="Search by title, requester or email"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              slotProps={{ input: {
                  startAdornment: (
                  <SearchIcon color="action" sx={{ marginRight: 1 }} />
                  ),
              }}}
              sx={{
                  width: '350px',
                  '& .MuiOutlinedInput-root': {
                  borderRadius: '10px', // Rounded corners
                  },
              }}
              />
          </Box>
        </Box>

        {loading && requests.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ borderRadius: '8px', marginBottom: '2rem' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                    <TableCell>Title</TableCell>
                    <TableCell>Requester</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date Submitted</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="h6" sx={{ textAlign: 'center', padding: '2rem' }}>
                          {searchTerm || statusFilter !== 'All' ? 'No matching requests found' : 'No Enhancement Requests Found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequests
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 'bold' }}>{request.title}</Typography>
                          </TableCell>
                          <TableCell>{request.requester_name}</TableCell>
                          <TableCell>{request.requester_email}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'inline-block',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                backgroundColor: statusColors[request.status] || '#e0e0e0',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              {request.status}
                            </Box>
                          </TableCell>
                          <TableCell>{formatEnhancementDate(request.created_at)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewDetails(request)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
          ActionsComponent={TablePaginationActions}
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRequests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {editMode ? 'Edit Request' : 'Request Details'}
          </Typography>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Grid container spacing={3} sx={{ padding: '1rem' }}>
              <Grid size={{ xs: 12 }}>
                {editMode ? (
                  <TextField
                    label="Title"
                    fullWidth
                    value={updatedRequest.title || ''}
                    onChange={handleInputChange}
                    name="title"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {selectedRequest.title}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Description
                </Typography>
                {editMode ? (
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={4}
                    value={updatedRequest.description || ''}
                    onChange={handleInputChange}
                    name="description"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {selectedRequest.description}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Requester Name
                </Typography>
                {editMode ? (
                  <TextField
                    label="Requester Name"
                    fullWidth
                    value={updatedRequest.requester_name || ''}
                    onChange={handleInputChange}
                    name="requester_name"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body1">
                    {selectedRequest.requester_name}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Requester Email
                </Typography>
                {editMode ? (
                  <TextField
                    label="Requester Email"
                    fullWidth
                    value={updatedRequest.requester_email || ''}
                    onChange={handleInputChange}
                    name="requester_email"
                    variant="outlined"
                    type="email"
                  />
                ) : (
                  <Typography variant="body1">
                    {selectedRequest.requester_email}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Status
                </Typography>
                {editMode ? (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={updatedRequest.status || 'Pending'}
                      onChange={handleInputChange}
                      name="status"
                      label="Status"
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Box
                    sx={{
                      display: 'inline-block',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: statusColors[selectedRequest.status] || '#e0e0e0',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    {selectedRequest.status}
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Date Submitted
                </Typography>
                <Typography variant="body1">
                  {formatEnhancementDate(selectedRequest.created_at)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Comment
                </Typography>
                {editMode ? (
                  <TextField
                    label="Comment"
                    fullWidth
                    multiline
                    rows={2}
                    value={updatedRequest.comment || ''}
                    onChange={handleInputChange}
                    name="comment"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {selectedRequest.comment || <span style={{color:'#888'}}>No comment</span>}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Attachments
                </Typography>
                {editMode ? (
                  <>
                    {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
                      <Box sx={{ marginBottom: '1rem' }}>
                        <Typography variant="body2" sx={{ marginBottom: '0.5rem' }}>
                          Current Attachments:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          {selectedRequest.attachments.map((attachment, index) => (
                            <Button
                              key={attachment.attachment_id}
                              variant="outlined"
                              size="small"
                              startIcon={getEnhancementFileIcon(attachment.attachment_name)}
                              color={getEnhancementFileColor(attachment.attachment_name)}
                              onClick={() => handleDownloadAttachment(
                                attachment.attachment_url,
                                attachment.attachment_name
                              )}
                              sx={{ 
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                py: 0.5,
                                px: 1,
                                minWidth: 'auto',
                                justifyContent: 'flex-start',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: 2,
                                },
                              }}
                            >
                              {attachment.attachment_name}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    )}
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="*"
                      multiple
                      style={{ display: 'none' }}
                      id="update-attachment"
                    />
                    <label htmlFor="update-attachment">
                      <Button variant="outlined" component="span" sx={{ marginRight: '1rem' }}>
                        {files.length > 0 ? 'Change Selected Files' : 'Select New Files'}
                      </Button>
                    </label>
                    {files.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {files.map((file, index) => (
                          <Typography key={index} variant="body2" sx={{ display: 'inline' }}>
                            {file.name}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                ) : selectedRequest.attachments && selectedRequest.attachments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedRequest.attachments.map((attachment, index) => (
                      <Button
                        key={attachment.attachment_id}
                        variant="outlined"
                        size="small"
                        startIcon={getEnhancementFileIcon(attachment.attachment_name)}
                        color={getEnhancementFileColor(attachment.attachment_name)}
                        onClick={() => handleDownloadAttachment(
                          attachment.attachment_url,
                          attachment.attachment_name
                        )}
                        sx={{ 
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1,
                          minWidth: 'auto',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                          },
                        }}
                      >
                        {attachment.attachment_name}
                      </Button>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.disabled">No attachments</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '1rem' }}>
          {loading && (
            <CircularProgress size={24} sx={{ marginRight: '1rem' }} />
          )}
          <Button
            variant="outlined"
            onClick={handleEditToggle}
            startIcon={editMode ? <CloseIcon /> : <EditIcon />}
            disabled={loading}
          >
            {editMode ? 'Cancel Edit' : 'Edit'}
          </Button>
          {editMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveChanges}
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              Save Changes
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ViewEnhancementRequest;